import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { PAYMENT_ENDPOINT, TRANSACTION_ENDPOINT } from '@/constants/Endpoints'
import { useUserLogin } from '@/stores/userLogin'
import { LOGGER } from '@/util/logger'
import { DONATION_FIELD_MAP } from '@/constants/ShoppingCartConstants'
import { getRouter } from '@/router'
import { useCompletedTransactionStore } from '@/stores/completedTransaction'

const MAX_CART_ITEMS = 95
const TEMP_DATA_IN_LOCALSTORE_KEY = '__tempData'

export const useShoppingCartStore = defineStore('shoppingCart', () => {
  const cartItems = ref([])
  const transactionError = ref('')
  const completedTransactionStore = useCompletedTransactionStore()
  let checkoutResponse = ref(null)

  const generateUuid = (cartItem) => {
    if (cartItem.serviceType === 'VRCOA') {
      return `${cartItem.serviceType}-${cartItem.titleNumber}-${cartItem.tagNumber}`
    }
    if (cartItem.titleNumber && !cartItem.idNumber) {
      return `${cartItem.serviceType}-${cartItem.titleNumber}`
    }
    return `${cartItem.serviceType}-${cartItem.idNumber || Date.now()}`
  }

  const normalizeDonationFlags = (cartItem) => {
    const overrides = Object.fromEntries(
      Object.values(DONATION_FIELD_MAP).map((apiField) => [apiField, cartItem[apiField] === true]),
    )
    return { ...cartItem, ...overrides }
  }

  const addItemToCart = async ({ transactionID, cartItem, tempTransactionDetails = {}, postUpdate = true }) => {
    const normalizedCartItem = normalizeDonationFlags(cartItem)
    const uuid = generateUuid(normalizedCartItem)

    if (postUpdate) {
      await axios.post(TRANSACTION_ENDPOINT.ADD_TO_CART, { transactionID, cartItem: normalizedCartItem })
    }

    const existingIndex = cartItems.value.findIndex((entry) => entry.tempTransactionDetails.uuid === uuid)
    if (existingIndex === -1) {
      if (cartItems.value.length >= MAX_CART_ITEMS) return false
      cartItems.value.push({
        transactionID,
        cartItem: normalizedCartItem,
        tempTransactionDetails: { ...tempTransactionDetails, uuid },
      })
    } else {
      cartItems.value[existingIndex] = {
        transactionID,
        cartItem: normalizedCartItem,
        tempTransactionDetails: { ...tempTransactionDetails, uuid },
      }
    }
    return true
  }

  const getCartData = async (transactionID) => {
    const response = await axios.get(TRANSACTION_ENDPOINT.GET_CART_DATA(transactionID), {
      __disableSpinner__: true,
    })
    const data = response.data.data
    const update = (item) => {
      const tempTransactionDetails = {
        addOrganDonor: item.addOrganDonor,
        addVeteransDesignation: item.addVeteransDesignation,
        renewalYears: item.renewalYears,
        feeData: { ...data.feeInformation[`${item.idNumber}_${item.serviceType}`] },
      }
      addItemToCart({ transactionID, cartItem: item, postUpdate: false, tempTransactionDetails })
    }
    data.driverServices?.map((item) => {
      update(item)
    })
    data.vehicleServices?.map((item) => {
      update(item)
    })
    checkoutResponse.value = {
      wid: data.wid,
      orderNumber: data.webOrderNumber,
      paymentTimestamp: data.paymentTimestamp,
      cardNumber: data.cardNumber,
      card: data.card,
      payProcessorStatus: data.payProcessorStatus,
      userEmailAddress: data.userEmailAddress,
    }
    return data.clientDriverDTO
  }

  const removeItemFromCart = async (uuid) => {
    const entry = cartItems.value.find((e) => e.tempTransactionDetails.uuid === uuid)
    if (!entry) return

    await axios.delete(TRANSACTION_ENDPOINT.DELETE_FROM_CART, {
      data: { transactionID: entry.transactionID, cartItem: entry.cartItem },
    })
    cartItems.value = cartItems.value.filter((e) => e.tempTransactionDetails.uuid !== uuid)
  }

  const cancelEdit = async (pendingEditUuid) => {
    if (!pendingEditUuid) return
    await removeItemFromCart(pendingEditUuid)
  }

  const clearCart = () => {
    cartItems.value = []
    checkoutResponse.value = null
  }

  const removeCoaItems = () => {
    cartItems.value = cartItems.value.filter((entry) => {
      return !['DLCOA', 'IDCOA', 'VRCOA'].includes(entry.cartItem.serviceType)
    })
  }

  const getCartItems = () => {
    return cartItems.value
  }

  const hasItems = computed(() => cartItems.value.length > 0)

  const checkout = async (userEmail, confirmEmail) => {
    transactionError.value = ''
    const userLogin = useUserLogin()
    const transactionID =
      userLogin.loginResponse?.transactionOutputDTO?.transactionID ??
      userLogin.vsLoginResponse?.transactionOutputDTO?.transactionID ??
      null
    const tempData = {
      loginPageType: userLogin.currentLoginPageType,
      transactionID,
      token: userLogin.currentToken,
      idNumber: userLogin.currentDlNumber || userLogin.currentTitleNumber,
    }
    localStorage.setItem(TEMP_DATA_IN_LOCALSTORE_KEY, JSON.stringify(tempData))
    const encyptedForm = await axios.post(PAYMENT_ENDPOINT.CHECKOUT, {
      userEmail: userEmail || null,
      confirmEmail: confirmEmail || null,
      transactionID,
      idNumber: userLogin.currentDlNumber || userLogin.currentTitleNumber,
    })
    document.getElementById('__PostForm')?.remove()
    const paymentFormWrapper = document.createElement('div')
    const data = encyptedForm.data.data
    if (data.status === 'ok') {
      checkoutResponse.value = { ...data }
      completedTransactionStore.commitFromCart()
      const router = getRouter()
      router.push({ name: 'TransactionComplete' })
      return
    }
    paymentFormWrapper.innerHTML = encyptedForm.data.data
    document.getElementsByTagName('body')[0].append(paymentFormWrapper)
    document.getElementById('__PostForm').submit()
  }

  const addItemsToCart = async (items) => {
    const userLogin = useUserLogin()
    const transactionID = userLogin.loginResponse?.transactionOutputDTO?.transactionID ?? null

    for (const item of items) {
      await addItemToCart({ ...item, transactionID })
    }
  }

  const getCheckoutStatus = async () => {
    let { loginPageType, transactionID, token, idNumber } = JSON.parse(
      localStorage.getItem(TEMP_DATA_IN_LOCALSTORE_KEY) ?? {},
    )

    if (!transactionID) {
      transactionError.value = 'Transaction details are missing.'
      return
    }
    const userLogin = useUserLogin()
    userLogin.setDataFromLocalStorage({ token, idNumber, loginPageType })
    localStorage.removeItem(TEMP_DATA_IN_LOCALSTORE_KEY)
    transactionID = +transactionID
    const id = setInterval(async () => {
      const response = await axios.post(
        PAYMENT_ENDPOINT.CHECKOUT_STATUS,
        {
          transactionID,
          idNumber: idNumber,
        },
        {
          __disableSpinner__: true,
        },
      )
      const data = response.data.data
      const router = getRouter()
      if (data.paymentStatus === 'COMPLETED_WITH_ERROR') {
        clearInterval(id)
        const thinDriver = await getCartData(transactionID)
        userLogin.setLoginResponses(thinDriver, transactionID)
        router.push({ name: 'ShoppingCart' })
      }

      if (data.paymentStatus === 'COMPLETED') {
        clearInterval(id)
        const thinDriver = await getCartData(transactionID)
        userLogin.setLoginResponses(thinDriver, transactionID)
        completedTransactionStore.commitFromCart()
        router.push({ name: 'TransactionComplete' })
      }
    }, 1500)
  }

  const downloadAndPrint = async (transactionID) => {
    if (!transactionID) throw new Error('Transaction ID is required')

    try {
      const response = await axios.get(TRANSACTION_ENDPOINT.DOWNLOAD_AND_PRINT(transactionID), {
        responseType: 'blob',
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = globalThis.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (err) {
      LOGGER.logException(err)
      throw err
    }
  }

  /**
   * Set the id Number as the current one. Ex: When Add another service for this record is clicked and multiple IDs are in the cart, this will set the idNumber that is clicked on.
   * @param {*} idNumber
   */
  const setCurrentIDNumber = (idNumber) => {
    const userLogin = useUserLogin()
    userLogin.currentDlNumber = idNumber
  }

  return {
    cartItems,
    addItemToCart,
    addItemsToCart,
    removeItemFromCart,
    cancelEdit,
    clearCart,
    removeCoaItems,
    getCartItems,
    hasItems,
    checkout,
    downloadAndPrint,
    checkoutResponse,
    setCurrentIDNumber,
    getCheckoutStatus,
    getCartData,
  }
})
